import * as React from "react";
import { FieldSchema, FormControlDependsOnThenEnum, IInputFieldProps } from "@generation/core";
import { useFormContext, useWatch } from "react-hook-form";
import { useDependsOnHook } from "./useDependsOnHook";

export interface OnChangeError {
  result: {
    key: string;
    value:
      | string
      | {
          [key: string]: string;
        };
  }[];
}

export function onChangeHoc<T extends IInputFieldProps<FieldSchema>>(Component: React.FunctionComponent<T>) {
  const component = (props: T) => {
    const { setValue, trigger, getValues } = useFormContext();

    const formFieldName = props.name.substring(0, props.name.lastIndexOf(".")) ?? "";

    const dependsOnResult = useDependsOnHook(formFieldName, props.field.dependsOn);

    if (props.field.onChange && dependsOnResult.disabled !== true) {
      const basePath = props.name.substring(0, props.name.lastIndexOf(".")) ?? "";
      const fieldValue = useWatch({ name: props.name });

      React.useEffect(() => {
        if (props.field.onChange) {
          const timer = setTimeout(() => {
            if (props.field.onChange) {
              props.field.onChange
                .afterChangeGetValuesToBeSet(basePath, fieldValue)
                .then((values) => {
                  values.forEach((value) => {
                    if (typeof value.value === "object") {
                      const formValues = getValues();
                      const currentFormValues = formValues[value.key];
                      setValue(value.key, { ...currentFormValues, ...value.value }, { shouldValidate: true });
                    } else {
                      setValue(value.key, value.value, { shouldValidate: true });
                    }

                    if (props.field.onChange?.triggerValidationAfterSetValues === true) {
                      trigger(props.name);
                    }
                  });
                })
                .catch((error: OnChangeError) => {
                  if (error.result) {
                    const values = error.result;
                    values.forEach((value) => {
                      if (typeof value.value === "object") {
                        const formValues = getValues();
                        let cleanedObject = {};
                        if (props.field.onChange?.clearOnError) {
                          for (let key in formValues[value.key]) {
                            cleanedObject[key] = "";
                          }
                          setValue(value.key, { ...cleanedObject, ...value.value }, { shouldValidate: true });
                        } else {
                          const currentFormValues = formValues[value.key];
                          setValue(
                            value.key,
                            { ...currentFormValues, ...value.value },
                            { shouldValidate: true }
                          );
                        }
                      } else {
                        if (props.field.onChange?.clearOnError) {
                          setValue(value.key, "", { shouldValidate: true });
                        } else {
                          setValue(value.key, value.value, { shouldValidate: true });
                        }
                      }

                      if (props.field.onChange?.triggerValidationAfterSetValues === true) {
                        trigger(props.name);
                      }
                    });
                  }
                });
            }
          }, props.field.onChange.timeoutInMilliseconds);

          return () => clearTimeout(timer);
        }
      }, [fieldValue]);
    }

    return <Component {...props} />;
  };

  component.displayName = "OnChangeHoc";

  return component;
}
